import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { MessageCircle } from "lucide-react";
// FAQPage JSON-LD schema is emitted from src/pages/Index.tsx (via SEOHead
// structuredData) so it lands reliably in the prerendered <head>. This
// section keeps the sr-only microdata fallback only.

// Strip markdown (links, bold, bullets) for plain-text use in JSON-LD and
// crawler fallback markup.
const stripMarkdown = (text: string): string =>
  text
    .replace(/\*\*\[([^\]]+)\]\([^)]+\)\*\*/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/^[\s]*[•\-]\s*/gm, "")
    .replace(/\n{2,}/g, "\n\n")
    .trim();

// Helper function to parse text with markdown-style formatting
const parseFormattedText = (text: string): (string | JSX.Element)[] => {
  const parts: (string | JSX.Element)[] = [];
  // Combined regex for bold links (**[text](url)**), bold (**text**), and links [text](url)
  const regex = /(\*\*\[([^\]]+)\]\(([^)]+)\)\*\*)|(\*\*([^*]+)\*\*)|(\[([^\]]+)\]\(([^)]+)\))/g;
  let lastIndex = 0;
  let match;
  let keyIndex = 0;
  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[1]) {
      // Bold link: **[text](url)** - bold and underlined
      parts.push(<a key={`boldlink-${keyIndex++}`} href={match[3]} target="_blank" rel="noopener noreferrer" className="font-semibold text-cta hover:text-cta/80 underline underline-offset-2 transition-colors">
          {match[2]}
        </a>);
    } else if (match[4]) {
      // Bold text: **text** - using teal/green color
      parts.push(<strong key={`bold-${keyIndex++}`} className="font-semibold text-cta">
          {match[5]}
        </strong>);
    } else if (match[6]) {
      // Link: [text](url) - using teal/green color
      parts.push(<a key={`link-${keyIndex++}`} href={match[8]} target="_blank" rel="noopener noreferrer" className="text-cta hover:text-cta/80 underline underline-offset-2 transition-colors">
          {match[7]}
        </a>);
    }
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : [text];
};

// Helper to check if paragraph is a bullet point
const isBulletPoint = (text: string): boolean => {
  return text.trim().startsWith('•') || text.trim().startsWith('-');
};

// Parse paragraph and handle bullet points
const renderParagraph = (paragraph: string, pIndex: number) => {
  // Split by bullet points (• or lines starting with •)
  const lines = paragraph.split('\n');
  const hasBullets = lines.some(line => isBulletPoint(line));
  if (hasBullets) {
    return <ul key={pIndex} className="space-y-2 ml-1">
        {lines.map((line, lIndex) => {
        const trimmedLine = line.trim();
        if (isBulletPoint(trimmedLine)) {
          // Remove bullet character and parse the rest
          const content = trimmedLine.replace(/^[•\-]\s*/, '');
          return <li key={lIndex} className="flex items-start gap-2">
                <span className="text-cta mt-1.5 text-xs">•</span>
                <span className="flex-1">{parseFormattedText(content)}</span>
              </li>;
        }
        return <span key={lIndex}>{parseFormattedText(line)}</span>;
      })}
      </ul>;
  }
  return <p key={pIndex}>{parseFormattedText(paragraph)}</p>;
};
const FAQSection = () => {
  const {
    t
  } = useLanguage();
  const faqs = [{
    question: t("faq.q1"),
    answer: t("faq.a1")
  }, {
    question: t("faq.q2"),
    answer: t("faq.a2")
  }, {
    question: t("faq.q6"),
    answer: t("faq.a6")
  }, {
    question: t("faq.q3"),
    answer: t("faq.a3")
  }, {
    question: t("faq.q4"),
    answer: t("faq.a4")
  }, {
    question: t("faq.q5"),
    answer: t("faq.a5")
  }];




  return <section
      id="faq"
      aria-labelledby="faq-heading"
      itemScope
      itemType="https://schema.org/FAQPage"
      className="py-16 lg:py-24 px-2 bg-background"
    >



      {/* Crawler-readable plain-text FAQ. Hidden from sighted users but
          present in the prerendered HTML before hydration so AI crawlers
          and SEO bots can parse the full Q&A without executing JS. */}
      <div className="sr-only" aria-hidden="true">
        <h2>Frequently Asked Questions</h2>
        <dl>
          {faqs.map((faq, index) => (
            <div
              key={`seo-${index}`}
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
            >
              <dt itemProp="name">{stripMarkdown(faq.question)}</dt>
              <dd
                itemScope
                itemProp="acceptedAnswer"
                itemType="https://schema.org/Answer"
              >
                <div itemProp="text">{stripMarkdown(faq.answer)}</div>
              </dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="container mx-auto max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 id="faq-heading" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-cta mb-4">
            {t("faq.title")}
          </h2>
          
        </div>

        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => <AccordionItem key={index} value={`item-${index}`} className="bg-card border border-border/50 rounded-xl px-6 shadow-sm">
                <AccordionTrigger className="text-left text-foreground hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-foreground/80 pb-5 leading-relaxed text-lg">
                  <div className="space-y-4">
                    {faq.answer.split('\n\n').map((paragraph, pIndex) => renderParagraph(paragraph, pIndex))}
                  </div>
                </AccordionContent>
               </AccordionItem>)}
          </Accordion>

          {/* Soft CTA */}
          <div className="mt-12 text-center p-6 rounded-xl bg-card border border-border/50">
            <p className="text-lg font-medium text-foreground mb-2">
              {t("faq.ctaTitle")}
            </p>
            <p className="text-muted-foreground mb-4">
              {t("faq.ctaDescription")}
            </p>
            <Link to="/contact-us">
              <Button variant="outline" size="lg" className="group">
                <MessageCircle className="mr-2 w-5 h-5" />
                {t("faq.ctaButton")}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
};
export default FAQSection;